import { computed, reactive } from "@vue/composition-api";

import useAxios from "@/hooks/useAxios";
import useProxy from "@/hooks/useProxy";

/** Identifie la Veille globale comme origine de la demande (cf. useGlobalWatchSubmit). */
const RMR_ORIGIN_PAGE_ID = 89;

/**
 * L'instance axios de l'app est créée SANS timeout : un appel qui ne revient
 * jamais laisserait un spinner infini. Une transcription est longue par nature
 * (téléchargement puis transcription de l'audio). Si le client abandonne, le
 * serveur va au bout et écrit dans son cache : rouvrir la même vidéo répond
 * alors immédiatement.
 */
const TRANSCRIPTION_TIMEOUT_MS = 180000;

/** Position INITIALE de « découper en angles » : au-delà, une vidéo porte en
 *  général plusieurs sujets. Le seuil qui fait autorité est côté back. */
const SPLIT_SUGGESTED_ABOVE_S = 180;

const MAX_ANGLES = 5;

/**
 * Dépliage UNITAIRE d'une vidéo de la Veille globale (T7, arbitrage C).
 *
 * La liste n'affiche que des métadonnées : rien n'est transcrit tant que
 * l'éditeur n'a pas ouvert une vidéo. Sélectionner dix vidéos déclencherait
 * dix transcriptions et dix appels LLM avant d'afficher quoi que ce soit —
 * rédhibitoire depuis que la transcription se fait à la demande (T6).
 *
 * Le parcours écrit, lui, n'est pas touché : il regroupe PLUSIEURS entrées NRD
 * sous un angle, là où une vidéo donne N angles d'UNE source avec ses bornes.
 * Ce sont deux notions d'angle différentes, d'où deux chemins distincts.
 */
export function useGlobalWatchVideo({ onSubmitted }) {
  const { axiosPost } = useAxios();
  const { store, router } = useProxy();

  const state = reactive({
    open: false,
    /** La ligne NRD dépliée, `null` hors dialogue. */
    item: null,
    /** Réponse de /source-preview : transcription, durée, time codes. */
    source: null,
    split: false,
    /** `[{ angle, start_s, end_s, selected }]` */
    angles: [],
    error: null,
    analyzing: false,
    proposing: false,
    sending: false,
    sentCount: 0,
    totalToSend: 0,
  });

  const snackbar = reactive({
    show: false,
    color: "success",
    message: "",
    actions: [],
    timeout: 6000,
  });

  // =>  COMPUTED
  const siteId = computed(
    () => Number(store.getters["aiFactory/getSelectedSites"] || 0) || null
  );

  const templateId = computed(
    () => Number(store.getters["aiFactory/getCustomPromptTemplate"] || 0) || null
  );

  const imageHandler = computed(
    () => store.getters["aiFactory/getGenerationImageHandler"] || "generate"
  );

  /** Sans time codes, il n'y a rien à découper (voie RapidAPI Instagram). */
  const canSplit = computed(() => !!state.source?.has_timestamps);

  const selectedAngles = computed(() =>
    state.angles.filter((angle) => angle.selected && angle.angle.trim())
  );

  const canPropose = computed(
    () => !!state.source && !state.proposing && !state.sending
  );

  const canSubmit = computed(
    () => selectedAngles.value.length > 0 && !state.sending
  );

  const sendProgress = computed(() =>
    state.totalToSend > 1 ? `${state.sentCount} / ${state.totalToSend}` : ""
  );

  const busy = computed(
    () => state.analyzing || state.proposing || state.sending
  );

  // =>  METHODS
  const reset = () => {
    state.item = null;
    state.source = null;
    state.split = false;
    state.angles = [];
    state.error = null;
  };

  const close = () => {
    if (busy.value) return;
    state.open = false;
    reset();
  };

  /**
   * Transcrit la vidéo ouverte et en propose les angles, d'un seul geste :
   * l'éditeur a choisi cette vidéo, il n'a pas à redemander chaque étape.
   */
  const analyze = async () => {
    state.analyzing = true;
    state.error = null;

    try {
      const { data } = await axiosPost(
        "/api/ai-factory/source-preview",
        { url: state.item.url, site_id: siteId.value },
        { timeout: TRANSCRIPTION_TIMEOUT_MS }
      );

      if (!data.success) {
        // Une source indisponible répond 200 avec son motif : vidéo privée,
        // bloquée ou muette. On l'affiche tel quel.
        state.error = data.source?.error || "Source indisponible";
        return;
      }

      state.source = data.source;
      state.split =
        !!data.source.has_timestamps &&
        Number(data.source.video_duration_s || 0) > SPLIT_SUGGESTED_ABOVE_S;

      await proposeAngles();
    } catch (error) {
      state.error =
        error.code === "ECONNABORTED"
          ? "La transcription dépasse le délai d'attente. Le serveur la termine de son côté : rouvrez cette vidéo dans une minute, la réponse sera immédiate."
          : error.response?.data?.detail || "L'analyse a échoué";
    } finally {
      state.analyzing = false;
    }
  };

  const openFor = async (item) => {
    reset();
    state.item = item;
    state.open = true;
    await analyze();
  };

  /**
   * `more` : « Proposer d'autres angles ». Les angles affichés partent en
   * exclusion et les nouveaux s'AJOUTENT, non cochés — on en veut d'autres,
   * pas un remplacement.
   */
  const proposeAngles = async ({ more = false } = {}) => {
    if (!state.source) return;

    state.proposing = true;
    state.error = null;

    const excluded = more
      ? state.angles.map((angle) => angle.angle.trim()).filter(Boolean)
      : [];

    try {
      const { data } = await axiosPost(
        "/api/ai-factory/video-angles",
        {
          url: state.source.url,
          site_id: siteId.value,
          max_angles: MAX_ANGLES,
          split: state.split,
          exclude_angles: excluded,
        },
        { timeout: TRANSCRIPTION_TIMEOUT_MS }
      );

      if (!data.success) {
        state.error = data.error || "Proposition d'angles impossible";
        return;
      }

      const proposed = (data.angles || []).map((angle) => ({
        angle: angle.angle,
        start_s: angle.start_s,
        end_s: angle.end_s,
        selected: !more,
      }));

      state.angles = more ? [...state.angles, ...proposed] : proposed;

      if (!proposed.length) {
        state.error = more
          ? "Aucun angle supplémentaire n'a pu être proposé."
          : "Aucun angle n'a pu être proposé pour cette vidéo.";
      }
    } catch (error) {
      state.error =
        error.code === "ECONNABORTED"
          ? "La proposition d'angles dépasse le délai d'attente. Relancez-la dans une minute."
          : error.response?.data?.detail || "La proposition d'angles a échoué";
    } finally {
      state.proposing = false;
    }
  };

  const proposeMoreAngles = () => proposeAngles({ more: true });

  const addAngle = () => {
    state.angles.push({ angle: "", start_s: null, end_s: null, selected: true });
  };

  const removeAngle = (index) => {
    state.angles.splice(index, 1);
  };

  const notifySent = (okCount, koCount) => {
    snackbar.message = koCount
      ? `${okCount} brouillon(s) demandé(s), ${koCount} en échec.`
      : `${okCount} brouillon(s) demandé(s). Les articles sont en file de génération.`;
    snackbar.color = koCount ? "warning" : "success";
    // Porte une action qui navigue : pas de disparition automatique.
    snackbar.timeout = -1;
    snackbar.actions = [
      {
        text: "OK",
        onClick: () =>
          router.push({
            name: "ai-factory-post-generation-list",
            query: siteId.value ? { site_id: siteId.value } : {},
          }),
      },
    ];
    snackbar.show = true;
  };

  /** L'endpoint de mise en file prend UN article par appel : on boucle. */
  const submit = async () => {
    const angles = selectedAngles.value;
    if (!angles.length) return;

    state.sending = true;
    state.sentCount = 0;
    state.totalToSend = angles.length;

    let okCount = 0;
    let koCount = 0;

    for (const angle of angles) {
      try {
        await axiosPost(
          "/api/ai-factory/add-to-post-generation-queue-and-flag-source",
          {
            nrd_id: state.item.id,
            request: {
              status: "draft",
              user_email: localStorage.getItem("userEmail") || null,
              image_handler: imageHandler.value,
              rmr_origin_page_id: RMR_ORIGIN_PAGE_ID,
              site_id: siteId.value,
              topic: angle.angle.trim(),
              ...(templateId.value
                ? { aif_custom_prompt_template_id: templateId.value }
                : {}),
              sources: [
                {
                  // L'URL part TELLE QUELLE : c'est par elle que le back
                  // retrouve la ligne NRD pour la marquer « utilisée ».
                  url: state.item.url,
                  // `content` volontairement ABSENT. La Veille globale envoie
                  // d'ordinaire la description NRD comme contenu, ce qui
                  // court-circuite le scraping (get_sources_text) : une vidéo
                  // partirait alors en rédaction avec sa légende RSS au lieu de
                  // sa parole, et sans la moindre erreur. En ne l'envoyant pas,
                  // la génération rescrape l'URL et retrouve la transcription
                  // dans le cache posé par l'analyse — sans nouvel appel.
                  source_type: "video",
                  // Seules les BORNES voyagent : c'est le serveur qui découpe
                  // le transcript. Deux angles d'une même vidéo donnent deux
                  // passages distincts sans rien recopier en base.
                  start_s: angle.start_s,
                  end_s: angle.end_s,
                },
              ],
            },
          }
        );
        okCount += 1;
      } catch (error) {
        console.error(error);
        koCount += 1;
      }
      state.sentCount += 1;
    }

    state.sending = false;
    state.totalToSend = 0;
    notifySent(okCount, koCount);

    if (okCount) {
      state.open = false;
      reset();
      if (typeof onSubmitted === "function") onSubmitted();
    }
  };

  // =>  DATA
  return {
    addAngle,
    analyze,
    busy,
    canPropose,
    canSplit,
    canSubmit,
    close,
    openFor,
    proposeAngles,
    proposeMoreAngles,
    removeAngle,
    selectedAngles,
    sendProgress,
    snackbar,
    state,
    submit,
  };
}
